import React, { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import MUIProvider from '~/contexts/MUIProvider';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

import { UnifiedAnalyticsThemeProvider } from '~/pages/AskRichardUnified/UnifiedAnalyticsThemeProvider';
import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type { LensCardData, LensFilterValues, LensPeriod } from '~/types/leLens';

import { ReportFilters, ReportTable } from './LiveReportParts';
import RmMonitorChart, { dayLabel } from './RmMonitorChart';
import { type Row, csv, download, number, ratio, sum, text } from './liveModel';
import { RM_EDITABLE_FIELDS, rmPageEditSearch } from './pageContext';

const PAGE_ID = 'rm-monitor';
const STATUSES = ['awaiting_dates', 'cancelled', 'completed'];
const CHECK_FROM = '2024-11-01';
const CHECK_TO = '2026-03-31';
const SOURCE = 'lux-analytics-prod.analytics.sales_with_sessions · Limited Time Lux Exclusive';

export function RmMonitorContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const view = params.get('rm_view') === 'detail' ? 'detail' : 'monitor';
  const range = params.get('rm_range') === 'custom' ? 'custom' : 'quarter';
  const from = params.get('rm_from') ?? '';
  const to = params.get('rm_to') ?? '';
  const statuses = params.has('rm_status_all')
    ? []
    : params.getAll('rm_status').length
      ? params.getAll('rm_status')
      : STATUSES;
  const vendors = params.getAll('rm_vendor');
  const lives = params.getAll('rm_live');
  const opportunities = params.getAll('rm_opp');
  const countryMode =
    params.get('rm_countries') === 'all' ? 'all' : params.getAll('rm_country').length ? 'custom' : 'saved';
  const countries = params.getAll('rm_country');
  const checkFrom = params.get('rm_cin') ?? CHECK_FROM;
  const checkTo = params.get('rm_cout') ?? CHECK_TO;
  const actionCountry = params.get('rm_country_action');
  const actionOpportunity = params.get('rm_opp_action');
  const actionDayPct = params.get('rm_day_pct');
  const actionDayDate = params.get('rm_day_date');
  const actionRoomOpp = params.get('rm_room_opp');
  const actionRoomType = params.get('rm_room_type');
  const asOf = sydneyToday();
  const set = (changes: Record<string, string | Array<string> | null>) => {
    const next = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([key, value]) => {
      next.delete(key);
      if (value === null) return;
      (Array.isArray(value) ? value : [value]).forEach((entry) => next.append(key, entry));
    });
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };
  const toggle = (key: string, value: string | null) => set({ [key]: params.get(key) === value ? null : value });
  const period: LensPeriod =
    range === 'custom' && /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to
      ? { window: { from, to }, comparison: null }
      : { anchor: 'today', window: { toDate: 'quarter' }, comparison: null };
  const filters = useMemo<LensFilterValues>(() => {
    const values: LensFilterValues = {
      as_of: [asOf],
      period,
      item_status: statuses,
      check_in: { from: checkFrom, to: checkTo },
    };
    if (vendors.length) values.vendor_name = vendors;
    if (lives.length) values.live_offer = lives;
    if (opportunities.length) values.offer_opportunity_name = opportunities;
    if (countryMode === 'all') values.offer_country_name = [];
    if (countryMode === 'custom') values.offer_country_name = countries;
    if (actionCountry) values.action_country = [actionCountry];
    if (actionOpportunity) values.action_opportunity = [actionOpportunity];
    if (actionDayPct) values.action_day_pct = [actionDayPct];
    if (actionDayDate) values.action_day_date = [actionDayDate];
    if (actionRoomOpp) values.action_room_opportunity = [actionRoomOpp];
    if (actionRoomType) values.action_room_type = [actionRoomType];
    return values;
  }, [
    asOf,
    period,
    statuses,
    checkFrom,
    checkTo,
    vendors,
    lives,
    opportunities,
    countryMode,
    countries,
    actionCountry,
    actionOpportunity,
    actionDayPct,
    actionDayDate,
    actionRoomOpp,
    actionRoomType,
  ]);
  const query = useGetReportingLeLensData(nativeLensId(PAGE_ID), filters);
  const cards = query.data?.cards ?? [];
  const options = query.data?.filterOptions ?? {};
  const needed = view === 'monitor' ? MONITOR_CARDS : DETAIL_CARDS;
  const loading = query.isFetching || query.isShowingPreviousData;
  const failed = cards.filter((card) => needed.includes(card.cardId) && card.status === 'error');
  const ready = !loading && !query.error && failed.length === 0 && needed.every((id) => readyCard(cards, id));
  usePageBridge({
    pageId: PAGE_ID,
    view: {
      title: view === 'detail' ? 'RM Monitor · Detailed Breakdown' : 'RM Monitor · Overview',
      ready,
      filterSchema: RM_EDITABLE_FIELDS,
      filters: {
        view,
        range,
        from,
        to,
        itemStatuses: statuses.map((value) => (value === '__NONE__' ? 'None' : value)),
        liveOffers: lives.map((value) => (value === '__NONE__' ? 'None' : value)),
        vendors: vendors.map((value) => (value === '__NONE__' ? 'None' : value)),
        opportunities: opportunities.map((value) => (value === '__NONE__' ? 'None' : value)),
        countryMode,
        countries,
        checkInFrom: checkFrom,
        checkInTo: checkTo,
        selectedCountry: actionCountry,
        selectedOpportunity: actionOpportunity,
        selectedPercentDay: actionDayPct,
        selectedDate: actionDayDate,
        selectedRoomOpportunity: actionRoomOpp,
        selectedRoomType: actionRoomType,
      },
      dataMode: 'live BigQuery through Lens',
      summary: {
        activeTab: view === 'detail' ? 'Detailed Breakdown' : 'RM Monitor',
        transactionPeriod: query.data?.period,
        totalGm: sum(rowsOf(cards, 'date_breakdown'), 'total_gm'),
      },
      definitions: {
        population:
          'Limited Time Lux Exclusive, transaction date before today, with the active Sydney transaction window and local filters shown above. The saved country mode uses the workbook country list.',
        localFilterContext:
          'Only view, range, from and to are editable through page chat. Other fields describe current UI filters and cross-selections. Empty member lists mean All; None means no members selected. The check-in range applies to its chart; chart and table selections retain their source worksheet scopes.',
        rmMargin: 'Surcharge internal sales AUD when item status is not cancelled.',
        totalGm: 'GM (AR) plus overrides GM (AR), both net of their refund fields, summed before the RM percent.',
        rmPercent: 'Sum of RM Margin (AC) divided by sum of Total GM (AR) at the displayed grain.',
      },
    },
    edit: (changes) => {
      if (!ready) throw new Error('Page data is still loading.');
      navigate({ pathname: location.pathname, search: rmPageEditSearch(location.search, changes) }, { replace: true });
    },
  });
  const dateRows = measureRows(rowsOf(cards, 'date_breakdown'), 'txn_date').sort((a, b) =>
    text(b.owner_tuple).localeCompare(text(a.owner_tuple)),
  );
  const countryRows = measureRows(rowsOf(cards, 'country_breakdown'), 'offer_country_name').sort(bySurcharge);
  const opportunityRows = measureRows(rowsOf(cards, 'opportunity_breakdown'), 'offer_opportunity_name').sort(
    bySurcharge,
  );
  const roomRows = rowsOf(cards, 'room_breakdown');
  const dealRows = rowsOf(cards, 'deal_details');
  return (
    <Container
      maxWidth={false}
      sx={{
        py: 3,
        fontFamily: FONT_STACK_SUISSE_INTL,
        '& .MuiTypography-root, & .MuiInputBase-root, & .MuiInputLabel-root, & .MuiButton-root, & .MuiTab-root, & .MuiChip-root, & .MuiTableCell-root, & .MuiTablePagination-root':
          { fontFamily: FONT_STACK_SUISSE_INTL },
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          sx={{
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'baseline' },
            gap: { xs: 0.5, sm: 2 },
          }}
        >
          <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography
              component="h1"
              variant="h4"
              sx={{ fontFamily: FONT_STACK_SUISSE_INTL, fontSize: { xs: 22, sm: 24 }, fontWeight: 600 }}
            >
              RM Monitor
            </Typography>
            <PageChatButton />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {query.data?.period?.label?.current ?? 'This quarter'} · excluding today · Sydney
          </Typography>
        </Stack>
        <Tabs
          value={view}
          onChange={(_, value) => set({ rm_view: value === 'monitor' ? null : 'detail' })}
          aria-label="RM Monitor views"
        >
          <Tab value="monitor" label="RM Monitor" />
          <Tab value="detail" label="Detailed Breakdown" />
        </Tabs>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            select
            size="small"
            label="Transaction date"
            value={range}
            onChange={(event) => set({ rm_range: event.target.value === 'quarter' ? null : 'custom' })}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="quarter">This quarter</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </TextField>
          {range === 'custom' && (
            <>
              <TextField
                size="small"
                type="date"
                label="From"
                value={from}
                onChange={(event) => set({ rm_from: event.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={to}
                onChange={(event) => set({ rm_to: event.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </>
          )}
          {view === 'detail' && (
            <>
              <TextField
                size="small"
                type="date"
                label="Check-in from"
                value={checkFrom}
                onChange={(event) => set({ rm_cin: event.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="date"
                label="Check-in to"
                value={checkTo}
                onChange={(event) => set({ rm_cout: event.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </>
          )}
        </Stack>
        <ReportFilters
          definitions={[
            ['item_status', 'Item status'],
            ['live_offer', 'Live offer'],
            ['vendor_name', 'Vendor'],
            ['offer_opportunity_name', 'Offer opportunity'],
          ]}
          values={{
            item_status: statuses,
            live_offer: lives,
            vendor_name: vendors,
            offer_opportunity_name: opportunities,
          }}
          options={{
            item_status: options.item_status ?? STATUSES,
            live_offer: options.live_offer ?? ['Ended', 'Live'],
            vendor_name: options.vendor_name ?? [],
            offer_opportunity_name: options.offer_opportunity_name ?? [],
          }}
          onChange={(key, value) => {
            if (key === 'item_status')
              set(value.length ? { rm_status: value, rm_status_all: null } : { rm_status: null, rm_status_all: '1' });
            if (key === 'live_offer') set({ rm_live: value.length ? value : null });
            if (key === 'vendor_name') set({ rm_vendor: value.length ? value : null });
            if (key === 'offer_opportunity_name') set({ rm_opp: value.length ? value : null });
          }}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignItems: { md: 'center' } }}>
          <Typography variant="body2" sx={{ minWidth: 120 }}>
            Offer country
          </Typography>
          <Chip
            label="Saved list"
            color={countryMode === 'saved' ? 'primary' : 'default'}
            onClick={() => set({ rm_countries: null, rm_country: null })}
          />
          <Chip
            label="All"
            color={countryMode === 'all' ? 'primary' : 'default'}
            onClick={() => set({ rm_countries: 'all', rm_country: null })}
          />
          <Autocomplete
            multiple
            size="small"
            options={options.offer_country_name ?? []}
            value={countryMode === 'custom' ? countries : []}
            onChange={(_, value) => set({ rm_countries: null, rm_country: value.length ? value : null })}
            renderInput={(field) => <TextField {...field} label="Choose countries" placeholder="Saved list" />}
            sx={{ flex: 1, minWidth: 240 }}
          />
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {actionCountry && (
            <Chip label={`Country · ${actionCountry}`} onDelete={() => set({ rm_country_action: null })} />
          )}
          {actionOpportunity && (
            <Chip label={`Opportunity · ${actionOpportunity}`} onDelete={() => set({ rm_opp_action: null })} />
          )}
          {actionDayPct && (
            <Chip label={`% Margin day · ${dayLabel(actionDayPct)}`} onDelete={() => set({ rm_day_pct: null })} />
          )}
          {actionDayDate && (
            <Chip label={`Date · ${dayLabel(actionDayDate)}`} onDelete={() => set({ rm_day_date: null })} />
          )}
          {actionRoomOpp && (
            <Chip
              label={`Room · ${actionRoomOpp}${actionRoomType ? ` / ${actionRoomType}` : ''}`}
              onDelete={() => set({ rm_room_opp: null, rm_room_type: null })}
            />
          )}
        </Stack>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && query.error && <Alert severity="error">{calm(text(query.error))}</Alert>}
        {!loading &&
          failed.map((card) => (
            <Alert key={card.cardId} severity="error">
              {card.cardId}: {calm(card.message ?? 'This card did not return.')}
            </Alert>
          ))}
        {!loading && !query.error && view === 'monitor' && (
          <Monitor
            cards={cards}
            dateRows={dateRows}
            countryRows={countryRows}
            opportunityRows={opportunityRows}
            actionCountry={actionCountry}
            actionOpportunity={actionOpportunity}
            actionDayDate={actionDayDate}
            onCountry={(value) => toggle('rm_country_action', value)}
            onOpportunity={(value) => toggle('rm_opp_action', value)}
            onDay={(value) => toggle('rm_day_date', value)}
            onPercentDay={(value) => toggle('rm_day_pct', value)}
          />
        )}
        {!loading && !query.error && view === 'detail' && (
          <Detail
            cards={cards}
            dateRows={dateRows}
            roomRows={roomRows}
            dealRows={dealRows}
            checkFrom={checkFrom}
            checkTo={checkTo}
            actionDayDate={actionDayDate}
            actionRoomOpp={actionRoomOpp}
            actionRoomType={actionRoomType}
            onDay={(value) => toggle('rm_day_date', value)}
            onRoom={(opportunity, room) => {
              if (actionRoomOpp === opportunity && (room ? actionRoomType === room : !actionRoomType)) {
                set({ rm_room_opp: null, rm_room_type: null });
                return;
              }
              set({ rm_room_opp: opportunity, rm_room_type: room });
            }}
          />
        )}
      </Stack>
    </Container>
  );
}

function Monitor({
  cards,
  dateRows,
  countryRows,
  opportunityRows,
  actionCountry,
  actionOpportunity,
  actionDayDate,
  onCountry,
  onOpportunity,
  onDay,
  onPercentDay,
}: {
  cards: Array<LensCardData>;
  dateRows: Array<Row>;
  countryRows: Array<Row>;
  opportunityRows: Array<Row>;
  actionCountry: string | null;
  actionOpportunity: string | null;
  actionDayDate: string | null;
  onCountry: (value: string) => void;
  onOpportunity: (value: string) => void;
  onDay: (value: string) => void;
  onPercentDay: (value: string) => void;
}) {
  const margin = ascending(rowsOf(cards, 'margin_trend'), 'txn_date');
  const percent = ascending(rowsOf(cards, 'pct_trend'), 'txn_date');
  const days = margin.map((row) => iso(row.txn_date));
  const percentDays = percent.map((row) => iso(row.txn_date));
  return (
    <Stack spacing={2}>
      <RmMonitorChart
        title="Margin Trend"
        categories={days}
        source={SOURCE}
        series={[
          { name: 'GM (AR)', data: margin.map((row) => number(row.gm_ar)), kind: 'line' },
          { name: 'RM Margin (AC)', data: margin.map((row) => number(row.rm_margin)), kind: 'line', axis: 'right' },
        ]}
      />
      <RmMonitorChart
        title="% Margin Trend"
        categories={percentDays}
        source={`${SOURCE} · select a day to filter the tables`}
        onSelect={onPercentDay}
        series={[
          {
            name: 'RM % of Total Flash',
            data: percent.map((row) => ratio(number(row.rm_margin), number(row.total_gm))),
            kind: 'line',
            percent: true,
          },
        ]}
      />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '1.15fr 0.85fr 1.15fr' } }}>
        <MeasureTable
          title="Date Breakdown"
          rows={dateRows}
          label="Day of transaction"
          selected={actionDayDate}
          onSelect={onDay}
        />
        <MeasureTable
          title="Offer Country Breakdown"
          rows={countryRows}
          label="Offer country"
          selected={actionCountry}
          onSelect={onCountry}
        />
        <MeasureTable
          title="Opportunity Breakdown"
          rows={opportunityRows}
          label="Offer opportunity"
          selected={actionOpportunity}
          onSelect={onOpportunity}
        />
      </Box>
    </Stack>
  );
}

function Detail({
  cards,
  dateRows,
  roomRows,
  dealRows,
  checkFrom,
  checkTo,
  actionDayDate,
  actionRoomOpp,
  actionRoomType,
  onDay,
  onRoom,
}: {
  cards: Array<LensCardData>;
  dateRows: Array<Row>;
  roomRows: Array<Row>;
  dealRows: Array<Row>;
  checkFrom: string;
  checkTo: string;
  actionDayDate: string | null;
  actionRoomOpp: string | null;
  actionRoomType: string | null;
  onDay: (value: string) => void;
  onRoom: (opportunity: string, room: string | null) => void;
}) {
  const weeks = ascending(rowsOf(cards, 'checkin_trend'), 'check_in_date');
  const roomTotals = groupSum(roomRows, 'offer_opportunity_name');
  const rooms = [...roomRows].sort((a, b) => {
    const opp =
      (roomTotals.get(text(b.offer_opportunity_name)) ?? 0) - (roomTotals.get(text(a.offer_opportunity_name)) ?? 0);
    return opp || (number(b.surcharge_aud) ?? 0) - (number(a.surcharge_aud) ?? 0);
  });
  const vendorTotals = groupSum(dealRows, 'vendor_name');
  const deals = [...dealRows].sort((a, b) => {
    const vendor = (vendorTotals.get(text(b.vendor_name)) ?? 0) - (vendorTotals.get(text(a.vendor_name)) ?? 0);
    if (vendor) return vendor;
    return (
      text(a.order_bk).localeCompare(text(b.order_bk)) ||
      text(a.booking_number).localeCompare(text(b.booking_number)) ||
      text(a.room_type_name).localeCompare(text(b.room_type_name)) ||
      text(a.check_in_date).localeCompare(text(b.check_in_date))
    );
  });
  return (
    <Stack spacing={2}>
      <RmMonitorChart
        title="RM Margin by Check In"
        categories={weeks.map((row) => iso(row.check_in_date))}
        source={`${SOURCE} · week starts Sunday · check-in ${checkFrom} to ${checkTo}`}
        series={[
          { name: 'RM Margin (AC)', data: weeks.map((row) => number(row.rm_margin)), kind: 'bar' },
          {
            name: 'RM % of Total Flash',
            data: weeks.map((row) => ratio(number(row.rm_margin), number(row.total_gm))),
            kind: 'line',
            axis: 'right',
            percent: true,
          },
        ]}
      />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '0.8fr 1.2fr' } }}>
        <MeasureTable
          title="Date Breakdown"
          rows={dateRows}
          label="Day of transaction"
          selected={actionDayDate}
          onSelect={onDay}
        />
        <RoomTable
          rows={rooms}
          truncated={cardOf(cards, 'room_breakdown')?.truncated}
          opportunity={actionRoomOpp}
          room={actionRoomType}
          onSelect={onRoom}
        />
      </Box>
      <ReportTable
        title="Deal Details"
        rows={deals.map((row) => ({
          vendor_name: text(row.vendor_name),
          order_bk: text(row.order_bk),
          booking_number: text(row.booking_number),
          room_type_name: text(row.room_type_name),
          check_in_date: dayLabel(iso(row.check_in_date)),
          los: number(row.los),
          'RM Margin (AC)': number(row.rm_margin),
        }))}
        columns={[
          { key: 'vendor_name', label: 'Vendor' },
          { key: 'order_bk', label: 'Order' },
          { key: 'booking_number', label: 'Booking number' },
          { key: 'room_type_name', label: 'Room type' },
          { key: 'check_in_date', label: 'Check-in' },
          { key: 'los', label: 'LOS', numeric: true },
          { key: 'RM Margin (AC)', label: 'RM Margin (AC)', numeric: true },
        ]}
        source={SOURCE}
        truncated={cardOf(cards, 'deal_details')?.truncated}
        exportColumns={[
          'vendor_name',
          'order_bk',
          'booking_number',
          'room_type_name',
          'check_in_date',
          'los',
          'RM Margin (AC)',
        ]}
      />
    </Stack>
  );
}

function MeasureTable({
  title,
  rows,
  label,
  selected,
  onSelect,
}: {
  title: string;
  rows: Array<Row>;
  label: string;
  selected: string | null;
  onSelect: (value: string) => void;
}) {
  const totalGm = sum(rows, 'Total GM (AR)');
  const rm = sum(rows, 'RM Margin (AC)');
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        Grand total {money(totalGm)} · RM {money(rm)} · {percentText(ratio(rm, totalGm))}
      </Typography>
      <ReportTable
        title={title}
        rows={rows}
        columns={[
          { key: 'label', label },
          { key: 'Total GM (AR)', label: 'Total GM (AR)', numeric: true },
          { key: 'RM Margin (AC)', label: 'RM Margin (AC)', numeric: true },
          { key: 'RM % of Total Flash', label: 'RM % of Total Flash', numeric: true, percent: true },
        ]}
        source={SOURCE}
        selected={selected ? [selected] : []}
        onSelect={(row) => onSelect(text(row.owner_tuple))}
        exportColumns={['label', 'Total GM (AR)', 'RM Margin (AC)', 'RM % of Total Flash']}
      />
    </Stack>
  );
}

function RoomTable({
  rows,
  truncated = false,
  opportunity,
  room,
  onSelect,
}: {
  rows: Array<Row>;
  truncated?: boolean;
  opportunity: string | null;
  room: string | null;
  onSelect: (opportunity: string, room: string | null) => void;
}) {
  const visible = rows.slice(0, 25);
  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Typography component="h2" variant="h6">
          Room Breakdown
        </Typography>
        <Button
          disabled={truncated}
          onClick={() =>
            download(
              'room-breakdown.csv',
              csv(
                rows.map((row) => ({
                  offer_opportunity_name: text(row.offer_opportunity_name),
                  room_type_name: text(row.room_type_name),
                  'Total GM (AR)': number(row.total_gm),
                  'RM Margin (AC)': number(row.rm_margin),
                  'RM % of Total Flash': ratio(number(row.rm_margin), number(row.total_gm)),
                  'Room Nights': number(row.number_of_nights),
                })),
                [
                  'offer_opportunity_name',
                  'room_type_name',
                  'Total GM (AR)',
                  'RM Margin (AC)',
                  'RM % of Total Flash',
                  'Room Nights',
                ],
              ),
            )
          }
        >
          Export CSV
        </Button>
      </Stack>
      {truncated && (
        <Alert severity="warning">
          Room results are incomplete. Narrow the filters or dates before exporting the complete scope.
        </Alert>
      )}
      <Table size="small" aria-label="Room Breakdown">
        <TableHead>
          <TableRow>
            {[
              'Offer opportunity',
              'Room type',
              'Total GM (AR)',
              'RM Margin (AC)',
              'RM % of Total Flash',
              'Room nights',
            ].map((label) => (
              <TableCell key={label}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((row, index) => {
            const opp = text(row.offer_opportunity_name);
            const type = text(row.room_type_name);
            const selected = opportunity === opp && (!room || room === type);
            return (
              <TableRow key={`${opp}-${type}-${index}`} selected={selected} hover>
                <TableCell>
                  <Button size="small" onClick={() => onSelect(opp, null)}>
                    {opp}
                  </Button>
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => onSelect(opp, type)}>
                    {type}
                  </Button>
                </TableCell>
                <TableCell align="right">{money(number(row.total_gm))}</TableCell>
                <TableCell align="right">{money(number(row.rm_margin))}</TableCell>
                <TableCell align="right">{percentText(ratio(number(row.rm_margin), number(row.total_gm)))}</TableCell>
                <TableCell align="right">{number(row.number_of_nights) ?? ''}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {rows.length > 25 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
          Showing 25 of {rows.length} returned rows.{!truncated && ' Export CSV includes all returned rows.'}
        </Typography>
      )}
    </Box>
  );
}

const MONITOR_CARDS = ['margin_trend', 'pct_trend', 'date_breakdown', 'country_breakdown', 'opportunity_breakdown'];
const DETAIL_CARDS = ['checkin_trend', 'date_breakdown', 'room_breakdown', 'deal_details'];

function rowsOf(cards: Array<LensCardData>, id: string) {
  return cardOf(cards, id)?.rows ?? [];
}
function cardOf(cards: Array<LensCardData>, id: string) {
  return cards.find((card) => card.cardId === id);
}
function readyCard(cards: Array<LensCardData>, id: string) {
  const card = cardOf(cards, id);
  return card?.status === 'ready' || card?.status === 'empty';
}
function iso(value: unknown) {
  return text(value).slice(0, 10);
}
function measureRows(rows: Array<Record<string, unknown>>, key: string): Array<Row> {
  const owner = (row: Record<string, unknown>) => (key === 'txn_date' ? iso(row[key]) : text(row[key]));
  return rows.map((row) => ({
    owner_tuple: owner(row),
    label: key === 'txn_date' ? dayLabel(iso(row[key])) : text(row[key]),
    'Total GM (AR)': number(row.total_gm),
    'RM Margin (AC)': number(row.rm_margin),
    'RM % of Total Flash': ratio(number(row.rm_margin), number(row.total_gm)),
    surcharge_aud: number(row.surcharge_aud) ?? 0,
  }));
}
function bySurcharge(a: Row, b: Row) {
  return number(b.surcharge_aud)! - number(a.surcharge_aud)!;
}
function ascending(rows: Array<Record<string, unknown>>, key: string) {
  return [...rows].sort((a, b) => iso(a[key]).localeCompare(iso(b[key])));
}
function groupSum(rows: Array<Record<string, unknown>>, key: string) {
  const totals = new Map<string, number>();
  rows.forEach((row) =>
    totals.set(text(row[key]), (totals.get(text(row[key])) ?? 0) + (number(row.surcharge_aud) ?? 0)),
  );
  return totals;
}
function money(value: number | null) {
  return value === null
    ? 'Unknown'
    : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(value);
}
function percentText(value: number | null) {
  return value === null
    ? 'Unknown'
    : new Intl.NumberFormat('en-AU', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
        value,
      );
}
function calm(message: string) {
  return /failed to (load|render)|something went wrong|taking longer than expected/i.test(message)
    ? 'This card did not return.'
    : message;
}
function sydneyToday() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export default function RmMonitorPage() {
  return (
    <MUIProvider>
      <UnifiedAnalyticsThemeProvider>
        <PageChatLayout>
          <RmMonitorContent />
        </PageChatLayout>
      </UnifiedAnalyticsThemeProvider>
    </MUIProvider>
  );
}
